import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Lock, PenLine, TrendingUp, MessageSquareText, Copy, Wand2, Check, Loader2,
  ScanText, Upload, AlertTriangle, CalendarClock, ShieldAlert,
} from "lucide-react";

const TOOLS = [
  { key: "listing", label: "Listing Writer", icon: PenLine },
  { key: "rent", label: "Rent Analyzer", icon: TrendingUp },
  { key: "comm", label: "Communication", icon: MessageSquareText },
  { key: "lease", label: "Lease Assistant", icon: ScanText },
];

const CopyBtn = ({ text, testid }) => {
  const [done, setDone] = useState(false);
  return (
    <button data-testid={testid} onClick={() => { navigator.clipboard.writeText(text); setDone(true); toast.success("Copied"); setTimeout(() => setDone(false), 1500); }}
      className="inline-flex items-center gap-1 text-xs text-mute hover:text-forest transition-colors">
      {done ? <Check className="h-3.5 w-3.5 text-forest" /> : <Copy className="h-3.5 w-3.5" />} Copy
    </button>
  );
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs font-medium text-mute">{label}</span>
    <input {...props} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" />
  </label>
);

export const AISuiteTab = ({ properties = [], onUpgrade }) => {
  const [locked, setLocked] = useState(false);
  const [ready, setReady] = useState(false);
  const [tool, setTool] = useState("listing");
  const [busy, setBusy] = useState(false);

  // Listing writer state
  const [lw, setLw] = useState({ property_id: "", title: "", city: "", state: "", bedrooms: "", bathrooms: "", rent: "", features: "", tone: "professional" });
  const [lwOut, setLwOut] = useState(null);
  // Rent analyzer state
  const [ra, setRa] = useState({ property_id: "", title: "", city: "", state: "", bedrooms: "", bathrooms: "", current_rent: "", features: "" });
  const [raOut, setRaOut] = useState(null);
  // Communication state
  const [cm, setCm] = useState({ type: "late_rent", tenant_name: "", property_name: "", context: "", tone: "professional" });
  const [cmOut, setCmOut] = useState(null);
  // Lease assistant state
  const [lzFile, setLzFile] = useState(null);
  const [lzOut, setLzOut] = useState(null);

  useEffect(() => {
    api.get("/ai/status").then((r) => { setLocked(!r.data.pro); setReady(true); }).catch(() => { setLocked(true); setReady(true); });
  }, []);

  const runListing = async () => {
    setBusy(true); setLwOut(null);
    try { const { data } = await api.post("/ai/listing-writer", { ...lw, bedrooms: lw.bedrooms || null, bathrooms: lw.bathrooms || null, rent: lw.rent || null }); setLwOut(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  const applyListing = async () => {
    try { await api.post("/ai/apply-listing", { property_id: lw.property_id, description: lwOut.description, title: lwOut.headlines?.[0] }); toast.success("Applied to your listing"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
  };
  const runRent = async () => {
    setBusy(true); setRaOut(null);
    try { const { data } = await api.post("/ai/rent-analyzer", { ...ra, bedrooms: ra.bedrooms || null, bathrooms: ra.bathrooms || null, current_rent: ra.current_rent || null }); setRaOut(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  const runComm = async () => {
    setBusy(true); setCmOut(null);
    try { const { data } = await api.post("/ai/communication", cm); setCmOut(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  const runLease = async () => {
    if (!lzFile) return;
    setBusy(true); setLzOut(null);
    try {
      const fd = new FormData(); fd.append("file", lzFile);
      const { data } = await api.post("/ai/lease-assistant", fd);
      setLzOut(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not review that file"); }
    finally { setBusy(false); }
  };

  if (!ready) return <div className="text-sm text-mute py-10 text-center">Loading…</div>;

  if (locked) {
    return (
      <div data-testid="ai-locked" className="max-w-xl mx-auto bg-white border border-line rounded-2xl p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center mx-auto"><Lock className="h-6 w-6 text-forest" /></div>
        <h2 className="text-xl font-heading font-medium text-ink mt-4">The Pro AI Suite is a Pro feature</h2>
        <p className="text-sm text-mute mt-2">Let AI write your listings, price your rentals, and draft tenant messages in seconds.</p>
        <ul className="text-sm text-ink text-left max-w-xs mx-auto mt-4 space-y-1.5">
          <li className="flex items-center gap-2"><PenLine className="h-4 w-4 text-forest" /> AI Listing Writer — descriptions, headlines, ads, social posts</li>
          <li className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-forest" /> AI Rent Analyzer — pricing & vacancy risk</li>
          <li className="flex items-center gap-2"><MessageSquareText className="h-4 w-4 text-forest" /> AI Communication Assistant</li>
          <li className="flex items-center gap-2"><ScanText className="h-4 w-4 text-forest" /> AI Lease Assistant — review lease PDFs</li>
        </ul>
        <button data-testid="ai-upgrade-btn" onClick={onUpgrade} className="mt-5 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors">Upgrade to Pro</button>
      </div>
    );
  }

  const propOptions = (
    <select value={tool === "listing" ? lw.property_id : ra.property_id}
      onChange={(e) => tool === "listing" ? setLw({ ...lw, property_id: e.target.value }) : setRa({ ...ra, property_id: e.target.value })}
      data-testid="ai-property-select" className="w-full border border-line rounded-lg px-3 py-2 text-sm">
      <option value="">Enter details manually…</option>
      {properties.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
    </select>
  );

  return (
    <div className="space-y-5" data-testid="ai-content">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-forest" />
        <h2 className="text-lg font-heading font-medium text-ink">Pro AI Suite</h2>
        <span className="text-[10px] font-semibold text-forest bg-forest/10 border border-forest/20 rounded-full px-2 py-0.5 uppercase">Pro</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {TOOLS.map((t) => (
          <button key={t.key} data-testid={`ai-tool-${t.key}`} onClick={() => setTool(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tool === t.key ? "bg-forest text-white" : "bg-white border border-line text-ink hover:bg-stone-50"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Listing Writer */}
      {tool === "listing" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-line rounded-xl p-5 space-y-3">
            <p className="text-sm font-medium text-ink">Property details</p>
            <div><span className="text-xs font-medium text-mute">Use an existing listing</span><div className="mt-1">{propOptions}</div></div>
            {!lw.property_id && (
              <>
                <Field label="Title" value={lw.title} onChange={(e) => setLw({ ...lw, title: e.target.value })} placeholder="Sunny 2BR near downtown" />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="City" value={lw.city} onChange={(e) => setLw({ ...lw, city: e.target.value })} />
                  <Field label="State" value={lw.state} onChange={(e) => setLw({ ...lw, state: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Beds" type="number" value={lw.bedrooms} onChange={(e) => setLw({ ...lw, bedrooms: e.target.value })} />
                  <Field label="Baths" type="number" value={lw.bathrooms} onChange={(e) => setLw({ ...lw, bathrooms: e.target.value })} />
                  <Field label="Rent" type="number" value={lw.rent} onChange={(e) => setLw({ ...lw, rent: e.target.value })} />
                </div>
              </>
            )}
            <label className="block"><span className="text-xs font-medium text-mute">Key features / amenities</span>
              <textarea data-testid="ai-lw-features" value={lw.features} onChange={(e) => setLw({ ...lw, features: e.target.value })} rows={2} placeholder="hardwood floors, in-unit laundry, pet friendly…" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm resize-none" /></label>
            <label className="block"><span className="text-xs font-medium text-mute">Tone</span>
              <select value={lw.tone} onChange={(e) => setLw({ ...lw, tone: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm">
                {["professional", "warm", "luxury", "casual", "energetic"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select></label>
            <button data-testid="ai-lw-generate" disabled={busy} onClick={runListing} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Generate listing copy
            </button>
          </div>
          <div className="space-y-3">
            {!lwOut ? <div className="bg-white border border-line border-dashed rounded-xl p-8 text-center text-sm text-mute">Your AI-written description, headlines, ad and social posts will appear here.</div> : (
              <>
                <div data-testid="ai-lw-description" className="bg-white border border-line rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1"><p className="text-sm font-medium text-ink">Description</p><CopyBtn text={lwOut.description} testid="ai-lw-desc-copy" /></div>
                  <p className="text-sm text-ink whitespace-pre-wrap">{lwOut.description}</p>
                  {lw.property_id && <button data-testid="ai-lw-apply" onClick={applyListing} className="mt-2 text-xs font-medium text-forest border border-forest/30 rounded-lg px-3 py-1.5 hover:bg-forest/5">Apply to this listing</button>}
                </div>
                <div className="bg-white border border-line rounded-xl p-4">
                  <p className="text-sm font-medium text-ink mb-1">Headlines</p>
                  {lwOut.headlines.map((h, i) => <div key={i} className="flex items-center justify-between text-sm text-ink py-0.5"><span>{h}</span><CopyBtn text={h} /></div>)}
                </div>
                <div className="bg-white border border-line rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1"><p className="text-sm font-medium text-ink">Rental ad</p><CopyBtn text={lwOut.ad} /></div>
                  <p className="text-sm text-ink whitespace-pre-wrap">{lwOut.ad}</p>
                </div>
                <div className="bg-white border border-line rounded-xl p-4">
                  <p className="text-sm font-medium text-ink mb-1">Social posts</p>
                  {lwOut.social_posts.map((s, i) => <div key={i} className="flex items-start justify-between gap-2 text-sm text-ink py-1 border-t border-line first:border-0"><span className="whitespace-pre-wrap">{s}</span><CopyBtn text={s} /></div>)}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Rent Analyzer */}
      {tool === "rent" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-line rounded-xl p-5 space-y-3">
            <p className="text-sm font-medium text-ink">Property details</p>
            <div><span className="text-xs font-medium text-mute">Use an existing listing</span><div className="mt-1">{propOptions}</div></div>
            {!ra.property_id && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="City" value={ra.city} onChange={(e) => setRa({ ...ra, city: e.target.value })} />
                  <Field label="State" value={ra.state} onChange={(e) => setRa({ ...ra, state: e.target.value })} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Beds" type="number" value={ra.bedrooms} onChange={(e) => setRa({ ...ra, bedrooms: e.target.value })} />
                  <Field label="Baths" type="number" value={ra.bathrooms} onChange={(e) => setRa({ ...ra, bathrooms: e.target.value })} />
                  <Field label="Current rent" type="number" value={ra.current_rent} onChange={(e) => setRa({ ...ra, current_rent: e.target.value })} />
                </div>
                <label className="block"><span className="text-xs font-medium text-mute">Features</span>
                  <input value={ra.features} onChange={(e) => setRa({ ...ra, features: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm" /></label>
              </>
            )}
            <button data-testid="ai-ra-generate" disabled={busy} onClick={runRent} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Analyze rent
            </button>
          </div>
          <div>
            {!raOut ? <div className="bg-white border border-line border-dashed rounded-xl p-8 text-center text-sm text-mute">A suggested rent range, vacancy-risk assessment and pricing tips will appear here.</div> : (
              <div data-testid="ai-ra-result" className="bg-white border border-line rounded-xl p-5 space-y-3">
                <div className="text-center">
                  <p className="text-xs text-mute">Suggested market rent</p>
                  <p className="text-3xl font-heading font-semibold text-forest">${Number(raOut.suggested_min || 0).toLocaleString()}–${Number(raOut.suggested_max || 0).toLocaleString()}<span className="text-sm text-mute">/mo</span></p>
                  <span className={`inline-block mt-1 text-xs font-semibold rounded-full px-2.5 py-0.5 border ${raOut.vacancy_risk === "low" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : raOut.vacancy_risk === "high" ? "text-red-700 bg-red-50 border-red-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>{raOut.vacancy_risk} vacancy risk</span>
                </div>
                <p className="text-sm text-ink">{raOut.rationale}</p>
                <div><p className="text-xs font-medium text-mute mb-1">Recommendations</p><ul className="space-y-1">{raOut.recommendations.map((r, i) => <li key={i} className="flex items-start gap-1.5 text-sm text-ink"><Check className="h-3.5 w-3.5 text-forest mt-0.5 shrink-0" />{r}</li>)}</ul></div>
                <p className="text-[11px] text-mute border-t border-line pt-2">AI estimate based on the details provided — not a formal appraisal.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Communication */}
      {tool === "comm" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-line rounded-xl p-5 space-y-3">
            <label className="block"><span className="text-xs font-medium text-mute">Message type</span>
              <select data-testid="ai-cm-type" value={cm.type} onChange={(e) => setCm({ ...cm, type: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm">
                <option value="late_rent">Late rent notice</option>
                <option value="lease_renewal">Lease renewal offer</option>
                <option value="maintenance_response">Maintenance response</option>
                <option value="move_out">Move-out instructions</option>
                <option value="showing_followup">Showing follow-up</option>
              </select></label>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Tenant name" value={cm.tenant_name} onChange={(e) => setCm({ ...cm, tenant_name: e.target.value })} />
              <Field label="Property" value={cm.property_name} onChange={(e) => setCm({ ...cm, property_name: e.target.value })} />
            </div>
            <label className="block"><span className="text-xs font-medium text-mute">Details / context</span>
              <textarea data-testid="ai-cm-context" value={cm.context} onChange={(e) => setCm({ ...cm, context: e.target.value })} rows={3} placeholder="e.g. July rent is 5 days late, $1,800 due plus $50 late fee" className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm resize-none" /></label>
            <label className="block"><span className="text-xs font-medium text-mute">Tone</span>
              <select value={cm.tone} onChange={(e) => setCm({ ...cm, tone: e.target.value })} className="mt-1 w-full border border-line rounded-lg px-3 py-2 text-sm">
                {["professional", "warm", "firm but kind", "concise"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select></label>
            <button data-testid="ai-cm-generate" disabled={busy} onClick={runComm} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Draft message
            </button>
          </div>
          <div>
            {!cmOut ? <div className="bg-white border border-line border-dashed rounded-xl p-8 text-center text-sm text-mute">Your drafted message will appear here, ready to copy.</div> : (
              <div data-testid="ai-cm-result" className="bg-white border border-line rounded-xl p-5 space-y-2">
                <div className="flex items-center justify-between"><p className="text-sm font-medium text-ink">Subject</p><CopyBtn text={cmOut.subject} /></div>
                <p className="text-sm text-ink">{cmOut.subject}</p>
                <div className="flex items-center justify-between border-t border-line pt-2"><p className="text-sm font-medium text-ink">Message</p><CopyBtn text={cmOut.message} testid="ai-cm-copy" /></div>
                <p className="text-sm text-ink whitespace-pre-wrap">{cmOut.message}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lease Assistant */}
      {tool === "lease" && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-line rounded-xl p-5 space-y-3">
            <p className="text-sm font-medium text-ink">Upload a lease PDF</p>
            <p className="text-xs text-mute">The AI reviews your lease and flags blank/missing fields, unsigned sections, recommended addendums, and the lease end date. Not legal advice.</p>
            <label data-testid="ai-lz-dropzone" className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-line rounded-xl p-6 cursor-pointer hover:border-forest/40 transition-colors">
              <Upload className="h-6 w-6 text-mute" />
              <span className="text-sm text-ink mt-2">{lzFile ? lzFile.name : "Choose a PDF file"}</span>
              <input data-testid="ai-lz-file" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { setLzFile(e.target.files?.[0] || null); setLzOut(null); }} />
            </label>
            <button data-testid="ai-lz-generate" disabled={busy || !lzFile} onClick={runLease} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanText className="h-4 w-4" />} Review lease
            </button>
          </div>
          <div>
            {!lzOut ? <div className="bg-white border border-line border-dashed rounded-xl p-8 text-center text-sm text-mute">Your lease review — missing fields, unsigned sections, suggested addendums and the end date — will appear here.</div> : (
              <div data-testid="ai-lz-result" className="bg-white border border-line rounded-xl p-5 space-y-3">
                {lzOut.summary && <p className="text-sm text-ink">{lzOut.summary}</p>}
                {lzOut.expiry?.end_date && (
                  <div className="flex items-center gap-2 text-sm text-ink rounded-lg bg-forest/[0.04] border border-forest/15 px-3 py-2">
                    <CalendarClock className="h-4 w-4 text-forest shrink-0" /> Lease ends <span className="font-medium">{lzOut.expiry.end_date}</span>{lzOut.expiry.note ? ` — ${lzOut.expiry.note}` : ""}
                  </div>
                )}
                {[["Missing / blank fields", lzOut.missing_fields, "text-amber-600"], ["Unsigned / undated sections", lzOut.unsigned_sections, "text-red-600"], ["Recommended addendums", lzOut.recommended_addendums, "text-forest"]].map(([label, items, tone], i) => (
                  <div key={i}>
                    <p className="text-xs font-medium text-mute mb-1">{label}</p>
                    {(!items || items.length === 0) ? <p className="text-xs text-mute">None found.</p> : (
                      <ul className="space-y-1">{items.map((it, j) => <li key={j} className="flex items-start gap-1.5 text-sm text-ink"><AlertTriangle className={`h-3.5 w-3.5 ${tone} mt-0.5 shrink-0`} />{it}</li>)}</ul>
                    )}
                  </div>
                ))}
                {lzOut.risk_flags && lzOut.risk_flags.length > 0 && (
                  <div data-testid="ai-lz-risks">
                    <p className="text-xs font-medium text-mute mb-1">Clause-level risk flags</p>
                    <div className="space-y-1.5">
                      {lzOut.risk_flags.map((r, j) => {
                        const tone = r.severity === "high" ? "border-red-200 bg-red-50" : r.severity === "low" ? "border-stone-200 bg-stone-50" : "border-amber-200 bg-amber-50";
                        const dot = r.severity === "high" ? "text-red-600" : r.severity === "low" ? "text-stone-500" : "text-amber-600";
                        return (
                          <div key={j} className={`rounded-lg border px-3 py-2 ${tone}`}>
                            <div className="flex items-center gap-1.5"><ShieldAlert className={`h-3.5 w-3.5 ${dot} shrink-0`} /><span className="text-sm font-medium text-ink">{r.clause}</span><span className={`text-[10px] font-semibold uppercase ${dot}`}>{r.severity}</span></div>
                            {r.risk && <p className="text-xs text-mute mt-0.5">{r.risk}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-mute border-t border-line pt-2">AI review to help you spot gaps — not legal advice.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
